import React, { ReactNode } from 'react';
import { Box, Paper, Typography } from '@material-ui/core';
import { PieChart, Pie, Cell, Tooltip, Sector, ResponsiveContainer, Label, TooltipProps } from 'recharts';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    // border: '1px solid rgba(255, 255, 255, 0.05)',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    height: '100%',
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#FFFFFF',
    textTransform: 'capitalize',
  },
  filterRow: {
    width: '100%',
    // minHeight: 56, // reserve space for contributor selector so charts align
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  chartContainer: {
    width: '100%',
    height: '100%',
    minHeight: 0,
    position: 'relative',
  },
  tooltipPaper: {
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    padding: theme.spacing(1.5),
  },
  tooltipName: {
    color: '#FFFFFF',
    marginBottom: 4,
    fontSize: 12,
    fontWeight: 600,
  },
  tooltipValue: {
    color: '#FFFFFF',
    fontWeight: 600,
    fontSize: 14,
  },
  tooltipCost: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 12,
    marginTop: 2,
  },
  percentageLabel: {
    fill: '#ffffff',
    fontSize: 12,
    fontWeight: 600,
    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))',
  },
  centerMainText: {
    fill: 'white',
    fontWeight: 600,
    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))',
  },
  centerSubText: {
    fill: 'rgba(255,255,255,0.6)',
    fontWeight: 500,
  },
  cellBase: {
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
  },
  cellInteractive: {
    cursor: 'pointer',
  },
  cellNonInteractive: {
    cursor: 'default',
  },
}));

interface LocationData {
  name: string;
  value: number;
  cost: number;
  color: string;
}

interface RolePieChartProps {
  role: string;
  totalTimeInHours: number;
  locationData: LocationData[];
  activeIndex: number | undefined;
  onMouseEnter: (index: number) => void;
  onMouseLeave: () => void;
  className?: string;
  filterElement?: ReactNode;
}

const CustomTooltipPie: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
  const classes = useStyles();
  if (active && payload && payload.length) {
    const data: any = payload[0].payload;
    const hours = Math.floor(data.value);
    const minutes = Math.round((data.value % 1) * 60);

    return (
      <Paper className={classes.tooltipPaper}>
        <Box>
          <Typography className={classes.tooltipName}>{data.name}</Typography>
          <Typography className={classes.tooltipValue}>
            {hours}h {minutes}m
          </Typography>
          <Typography className={classes.tooltipCost}>
            ${data.cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </Typography>
        </Box>
      </Paper>
    );
  }
  return null;
};

const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
    </g>
  );
};

const RolePieChart: React.FC<RolePieChartProps> = ({
  role,
  totalTimeInHours,
  locationData,
  activeIndex,
  onMouseEnter,
  onMouseLeave,
  className,
  filterElement,
}) => {
  const classes = useStyles();
  const hours = Math.floor(totalTimeInHours);
  const minutes = Math.round((totalTimeInHours % 1) * 60);

  const hasData = locationData.length > 0;
  const displayData = hasData ? locationData : [{ name: 'No data', value: 1, cost: 0, color: 'rgba(255,255,255,0.1)' }];

  // Re-implement label rendering to match the provided design (inside the donut)
  const RADIAN = Math.PI / 180;
  const renderPercentageLabel = (props: any) => {
    if (Math.abs(props.endAngle - props.startAngle) < 10) return null;

    const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} textAnchor="middle" dominantBaseline="central" className={classes.percentageLabel}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Center label that scales text to fit inside inner circle
  const renderCenterLabel = (props: any) => {
    const { viewBox } = props || {};
    const { cx, cy, innerRadius = 80, outerRadius = 120 } = viewBox || {};
    const mainText = `${hours}h ${minutes}m`;
    const availableWidth = innerRadius * 2 * 0.82; // extra padding from inner circle
    const approxCharWidth = 0.6; // rough width multiplier for this font
    const widthBasedFont = availableWidth / Math.max(1, mainText.length * approxCharWidth);
    const radiusBasedFont = outerRadius * 0.28;
    const mainSize = Math.max(10, Math.min(widthBasedFont, radiusBasedFont));
    const subSize = Math.max(9, Math.round(mainSize * 0.5));

    return (
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">
        <tspan x={cx} dy="-0.15em" fontSize={Math.round(mainSize)} className={classes.centerMainText}>
          {mainText}
        </tspan>
        <tspan x={cx} dy="1.75em" fontSize={Math.round(subSize)} className={classes.centerSubText}>
          Total
        </tspan>
      </text>
    );
  };

  return (
    <Box className={`${classes.card} ${className || ''}`}>
      <Typography variant="h6">{role.charAt(0) + role.slice(1).toLowerCase()}</Typography>

      <Box className={classes.filterRow}>{filterElement || null}</Box>

      <Box className={classes.chartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={displayData}
              cx="50%"
              cy="50%"
              innerRadius="65%"
              outerRadius="90%"
              paddingAngle={hasData ? 2 : 0}
              dataKey="value"
              stroke="none"
              startAngle={90}
              endAngle={-270}
              onMouseEnter={(_, index) => onMouseEnter(index)}
              onMouseLeave={onMouseLeave}
              activeIndex={activeIndex}
              activeShape={hasData ? renderActiveShape : undefined}
              labelLine={false}
              label={hasData ? renderPercentageLabel : undefined}
            >
              {displayData.map((entry: any, index: number) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  className={`${classes.cellBase} ${hasData ? classes.cellInteractive : classes.cellNonInteractive}`}
                />
              ))}
              <Label content={renderCenterLabel} />
            </Pie>
            {hasData && <Tooltip content={<CustomTooltipPie />} cursor={{ fill: 'rgba(255,255,255,0.15)' }} />}
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default RolePieChart;
